// Registry + gating por estado dos 5 formulários 🟢 do Pass 1.
//
// Decide QUAIS formulários aparecem para o estado atual do caso. Puro (sem IO):
// a UI mapeia o resultado em botões; o fetch de membros é lazy (só ao clicar
// num formulário com needsMembers).

import type { DenunciaState } from '@aions/core';
import type { FormBlock } from './form-pdf.js';
import type { FormInput } from './forms-common.js';
import { buildSigilo } from './form-sigilo.js';
import { buildInstauracao } from './form-instauracao.js';
import { buildTratativa } from './form-tratativa.js';
import { buildEncerramento } from './form-encerramento.js';
import { buildCustodia } from './form-custodia.js';

export type FormKey = 'sigilo' | 'instauracao' | 'tratativa' | 'encerramento' | 'custodia';

export interface FormDescriptor {
  key: FormKey;
  build: (input: FormInput) => FormBlock[];
  /** true → precisa do diretório de membros (fetch lazy ao clicar). */
  needsMembers: boolean;
  /** Base do nome do arquivo baixado (sufixado com o caseId na UI). */
  fileBase: string;
}

const ALL: Readonly<Record<FormKey, FormDescriptor>> = {
  sigilo: { key: 'sigilo', build: buildSigilo, needsMembers: true, fileBase: 'termo-sigilo' },
  instauracao: {
    key: 'instauracao',
    build: buildInstauracao,
    needsMembers: true,
    fileBase: 'termo-instauracao',
  },
  tratativa: {
    key: 'tratativa',
    build: buildTratativa,
    needsMembers: true,
    fileBase: 'tratativa-denuncias',
  },
  encerramento: {
    key: 'encerramento',
    build: buildEncerramento,
    needsMembers: true,
    fileBase: 'termo-encerramento',
  },
  custodia: {
    key: 'custodia',
    build: buildCustodia,
    needsMembers: false,
    fileBase: 'cadeia-custodia',
  },
};

// "Instrução em diante" — exclui Aberta/Triagem (investigação ainda não instaurada).
const INSTRUCAO_PLUS: ReadonlySet<string> = new Set([
  'Instrucao',
  'Oitivas',
  'AvaliacaoEmCurso',
  'AguardandoDecisao',
  'Decisao',
  'Recursal',
  'Encerrada',
]);

// Encerramento — só após a decisão emitida.
const ENCERRAMENTO_KINDS: ReadonlySet<string> = new Set(['Decisao', 'Recursal', 'Encerrada']);

/**
 * Formulários aplicáveis ao estado atual:
 *   - #8 Sigilo: sempre.
 *   - #1 Instauração / #3 Tratativa: de Instrução em diante.
 *   - #7 Encerramento: {Decisao, Recursal, Encerrada}.
 *   - #5 Custódia: se houver anexos.
 */
export function formsAplicaveis(state: DenunciaState, attachmentsCount: number): FormDescriptor[] {
  const out: FormDescriptor[] = [ALL.sigilo];
  if (INSTRUCAO_PLUS.has(state.kind)) out.push(ALL.instauracao, ALL.tratativa);
  if (ENCERRAMENTO_KINDS.has(state.kind)) out.push(ALL.encerramento);
  if (attachmentsCount > 0) out.push(ALL.custodia);
  return out;
}
